import React, { useEffect, useState } from 'react';
import { Target, Plus, Trash2, CheckCircle, Archive, ChevronDown, ChevronUp } from 'lucide-react';
import { apiClient } from '../services/api';
import { DailyReviewGoal, GoalStatus } from '../types/api';
import { CreateGoalModal } from './CreateGoalModal';

export const GoalsSection: React.FC = () => {
  const [goals, setGoals] = useState<DailyReviewGoal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedGoalId, setExpandedGoalId] = useState<number | null>(null);
  const [goalDetail, setGoalDetail] = useState<Record<number, DailyReviewGoal>>({});

  const loadGoals = async () => {
    try {
      const data = await apiClient.listDailyReviewGoals('active');
      setGoals(data);
      const details = await Promise.all(data.map(g => apiClient.getDailyReviewGoal(g.id)));
      const detailMap: Record<number, DailyReviewGoal> = {};
      details.forEach(d => { detailMap[d.id] = d; });
      setGoalDetail(detailMap);
    } catch {
      // silent
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadGoals();
  }, []);

  const toggleExpand = async (goal: DailyReviewGoal) => {
    if (expandedGoalId === goal.id) {
      setExpandedGoalId(null);
      return;
    }
    setExpandedGoalId(goal.id);
    if (!goalDetail[goal.id]) {
      try {
        const detail = await apiClient.getDailyReviewGoal(goal.id);
        setGoalDetail(prev => ({ ...prev, [goal.id]: detail }));
      } catch {
        // silent
      }
    }
  };

  const updateStatus = async (goal: DailyReviewGoal, status: GoalStatus) => {
    try {
      await apiClient.updateDailyReviewGoalStatus(goal.id, status);
      setGoals(prev => prev.filter(g => g.id !== goal.id));
      if (expandedGoalId === goal.id) setExpandedGoalId(null);
    } catch {
      // silent
    }
  };

  const deleteGoal = async (goal: DailyReviewGoal) => {
    try {
      await apiClient.deleteDailyReviewGoal(goal.id);
      setGoals(prev => prev.filter(g => g.id !== goal.id));
      if (expandedGoalId === goal.id) setExpandedGoalId(null);
    } catch {
      // silent
    }
  };

  const handleGoalCreated = (goal: DailyReviewGoal) => {
    setGoals(prev => [goal, ...prev]);
    setShowCreateModal(false);
  };

  if (isLoading) return null;

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Target className="h-5 w-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-800">Daily Goals</h2>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          <Plus className="h-4 w-4" />
          <span>New goal</span>
        </button>
      </div>

      {goals.length === 0 ? (
        <div className="bg-gray-50 border border-dashed border-gray-200 rounded-lg p-5 text-center">
          <p className="text-sm text-gray-500">No active goals. Set a daily card review target to track your progress.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {goals.map(goal => {
            const detail = goalDetail[goal.id];
            const today = new Date().toISOString().split('T')[0];
            const todayDay = detail?.days?.find(d => d.date === today);
            const reviewed = todayDay?.cards_reviewed ?? 0;
            const pct = Math.min(100, Math.round((reviewed / goal.daily_target) * 100));
            const isExpanded = expandedGoalId === goal.id;

            return (
              <div key={goal.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                {/* Summary row */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-sm font-medium text-gray-800">
                        {goal.daily_target} cards/day
                      </span>
                      <span className="text-xs text-gray-400 ml-2">
                        {goal.duration_days}d window · started {new Date(goal.start_date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => updateStatus(goal, 'completed')}
                        title="Mark completed"
                        className="p-1.5 text-gray-400 hover:text-green-600 rounded transition-colors"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => updateStatus(goal, 'archived')}
                        title="Archive"
                        className="p-1.5 text-gray-400 hover:text-yellow-600 rounded transition-colors"
                      >
                        <Archive className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteGoal(goal)}
                        title="Delete"
                        className="p-1.5 text-gray-400 hover:text-red-500 rounded transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => toggleExpand(goal)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors"
                      >
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Today's progress bar */}
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 bg-gray-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${pct >= 100 ? 'bg-green-500' : 'bg-primary-500'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {reviewed} / {goal.daily_target} today
                    </span>
                  </div>
                </div>

                {/* Expanded day-by-day view */}
                {isExpanded && detail?.days && (
                  <div className="border-t border-gray-100 px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {detail.days.map(day => {
                        const isPast = day.date < today;
                        const isToday = day.date === today;
                        const isFuture = day.date > today;
                        let bg = 'bg-gray-100 text-gray-400';
                        if (isFuture) bg = 'bg-gray-50 text-gray-300 border border-dashed border-gray-200';
                        else if (day.hit_target) bg = 'bg-green-100 text-green-700';
                        else if (isPast) bg = 'bg-red-50 text-red-400';
                        else if (isToday) bg = pct >= 100 ? 'bg-green-100 text-green-700' : 'bg-primary-50 text-primary-600 ring-1 ring-primary-300';

                        const label = new Date(day.date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                        return (
                          <div
                            key={day.date}
                            title={`${label}: ${day.cards_reviewed}/${day.target}`}
                            className={`rounded px-2 py-1 text-xs font-medium ${bg}`}
                          >
                            {label}
                            {!isFuture && (
                              <span className="ml-1 opacity-70">{day.cards_reviewed}/{day.target}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreateModal && (
        <CreateGoalModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleGoalCreated}
        />
      )}
    </div>
  );
};
